import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import nacl from 'tweetnacl';
import { encodeBase64, decodeBase64 } from 'tweetnacl-util';

const strToBytes = (s) => new TextEncoder().encode(s);
const bytesToStr = (b) => new TextDecoder().decode(b);
import { messagingAPI } from '../services/api';
import { useAuth } from './AuthContext';
import { API_URL } from '../services/api';
import { toast } from 'react-toastify';

const MessagingContext = createContext(null);

export const useMessaging = () => {
  const ctx = useContext(MessagingContext);
  if (!ctx) throw new Error('useMessaging must be used within MessagingProvider');
  return ctx;
};

// ─── Shared key derivation ────────────────────────────────────────────────────
// Key is derived from the stable conversation ID so it never changes when
// members are added or removed.
const getConvKey = (convId) => {
  const hash = nacl.hash(strToBytes(String(convId)));
  return hash.slice(0, nacl.secretbox.keyLength);
};

const encryptMsg = (plaintext, convId) => {
  const key = getConvKey(convId);
  const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);
  const box = nacl.secretbox(strToBytes(plaintext), nonce, key);
  return {
    ciphertext: encodeBase64(box),
    nonce: encodeBase64(nonce),
    senderPublicKey: 'secretbox',
  };
};

const decryptMsg = (ciphertextB64, nonceB64, convId) => {
  try {
    const key = getConvKey(convId);
    const box = nacl.secretbox.open(decodeBase64(ciphertextB64), decodeBase64(nonceB64), key);
    if (!box) return null;
    return bytesToStr(box);
  } catch {
    return null;
  }
};

// ─── Provider ─────────────────────────────────────────────────────────────────
export const MessagingProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [directory, setDirectory] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState({});
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const socketRef = useRef(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const sentCache = useRef({});
  const conversationsRef = useRef([]);
  const activeConvIdRef = useRef(null);
  const userRef = useRef(user);

  useEffect(() => { conversationsRef.current = conversations; }, [conversations]);
  useEffect(() => { activeConvIdRef.current = activeConvId; }, [activeConvId]);
  useEffect(() => { userRef.current = user; }, [user]);

  // ── Socket.io connection ───────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    const SOCKET_URL = API_URL.replace(/\/api\/?$/, '');

    const socket = io(SOCKET_URL, {
      auth: { token: localStorage.getItem('token') },
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('join', { userId: user.id, tenantId: user.tenantId });
      setSocketConnected(true);
    });

    // ── New message ───
    socket.on('new_message', ({ conversationId, message }) => {
      setMessages((prev) => {
        const existing = prev[conversationId] || [];
        if (existing.some((m) => m._id === message._id)) return prev;
        return { ...prev, [conversationId]: [...existing, message] };
      });
      setConversations((prev) =>
        prev.map((c) => {
          if (String(c._id) !== String(conversationId)) return c;
          const isActive = String(conversationId) === String(activeConvIdRef.current);
          const preview = message.messageType === 'gif' ? 'GIF'
            : message.messageType === 'file' ? 'File'
            : message.messageType === 'audio' ? 'Voice message'
            : 'Encrypted message';
          return {
            ...c,
            lastMessage: { senderId: message.senderId, preview, timestamp: message.createdAt },
            unread: isActive ? 0 : (c.unread || 0) + 1,
          };
        })
      );

      // Auto-confirm delivery for messages in active conversation
      const myId = String(userRef.current?.id || '');
      const senderId = String(message.senderId?._id || message.senderId);
      if (senderId !== myId) {
        socket.emit('messages_received', { conversationId, userId: myId });
        // If chat open, instantly mark as read (WhatsApp-style auto seen)
        if (String(conversationId) === String(activeConvIdRef.current)) {
          messagingAPI.markRead(conversationId).catch(() => {});
        }
      }
    });

    // ── Reaction update ───
    socket.on('reaction_updated', ({ conversationId, messageId, reactions }) => {
      setMessages((prev) => {
        const existing = prev[conversationId];
        if (!existing) return prev;
        return {
          ...prev,
          [conversationId]: existing.map((m) =>
            String(m._id) === String(messageId) ? { ...m, reactions } : m
          ),
        };
      });
    });

    // ── Thread update ───
    socket.on('thread_updated', ({ conversationId, threadId, message }) => {
      // Update thread count on root message
      setMessages((prev) => {
        const existing = prev[conversationId];
        if (!existing) return prev;
        return {
          ...prev,
          [conversationId]: existing.map((m) =>
            String(m._id) === String(threadId)
              ? { ...m, threadCount: (m.threadCount || 0) + 1, threadLastReplyAt: message.createdAt }
              : m
          ),
        };
      });
    });

    // ── Messages read ───
    socket.on('messages_read', ({ conversationId, readBy, readAt }) => {
      setMessages((prev) => {
        const existing = prev[conversationId];
        if (!existing) return prev;
        return {
          ...prev,
          [conversationId]: existing.map((m) => {
            const alreadyRead = m.readBy?.some((r) => String(r.userId) === String(readBy));
            if (alreadyRead) return m;
            return {
              ...m,
              status: 'read',
              readBy: [...(m.readBy || []), { userId: readBy, readAt }],
            };
          }),
        };
      });
    });

    // ── Messages delivered ───
    socket.on('messages_delivered', ({ conversationId, deliveredTo, deliveredAt }) => {
      setMessages((prev) => {
        const existing = prev[conversationId];
        if (!existing) return prev;
        return {
          ...prev,
          [conversationId]: existing.map((m) => {
            const alreadyDelivered = m.deliveredTo?.some((d) => String(d.userId) === String(deliveredTo));
            if (alreadyDelivered) return m;
            return {
              ...m,
              status: m.status === 'read' ? 'read' : 'delivered',
              deliveredTo: [...(m.deliveredTo || []), { userId: deliveredTo, deliveredAt }],
            };
          }),
        };
      });
    });

    // ── Mention notification ───
    socket.on('mention_notification', ({ conversationId, mentionedBy }) => {
      const conv = conversationsRef.current.find((c) => String(c._id) === String(conversationId));
      const mentioner = conv?.participants?.find((p) => String(p._id) === String(mentionedBy));
      toast.info(`${mentioner?.username || 'Someone'} mentioned you`);
    });

    socket.on('conversation_updated', () => refreshConversations());

    socket.on('user_status_changed', ({ userId, status }) => {
      setDirectory((prev) =>
        prev.map((u) => (String(u._id) === String(userId) ? { ...u, status } : u))
      );
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setSocketConnected(false);
    };
  }, [isAuthenticated, user?.id]);

  // ── Join conversation room ────────────────────────────────────────────────
  useEffect(() => {
    if (!socketRef.current || !activeConvId) return;
    socketRef.current.emit('join_conversation', { conversationId: activeConvId });
    messagingAPI.markRead(activeConvId).catch(() => {});
    messagingAPI.markDelivered(activeConvId).catch(() => {});
    setConversations((prev) =>
      prev.map((c) => (String(c._id) === String(activeConvId) ? { ...c, unread: 0 } : c))
    );
    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave_conversation', { conversationId: activeConvId });
      }
    };
  }, [activeConvId]);

  // ── Total unread badge ─────────────────────────────────────────────────────
  useEffect(() => {
    setTotalUnread(conversations.reduce((sum, c) => sum + (c.unread || 0), 0));
  }, [conversations]);

  // ── Load directory + conversations on auth ─────────────────────────────────
  const loadDirectory = useCallback(async () => {
    try {
      const res = await messagingAPI.getDirectory();
      setDirectory(res.data.users || []);
    } catch (_) {}
  }, []);

  const refreshConversations = useCallback(async () => {
    try {
      const res = await messagingAPI.getConversations();
      setConversations(res.data.conversations || []);
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    loadDirectory();
    refreshConversations();
  }, [isAuthenticated]);

  // ── Load messages ──────────────────────────────────────────────────────────
  const loadMessages = useCallback(async (conversationId) => {
    setLoadingMessages(true);
    try {
      const res = await messagingAPI.getMessages(conversationId);
      setMessages((prev) => ({ ...prev, [conversationId]: res.data.messages || [] }));
    } catch (_) {
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    if (!activeConvId) return;
    loadMessages(activeConvId);
  }, [activeConvId]);

  // ── Open / create DM conversation ──────────────────────────────────────────
  const openConversation = useCallback(async (participantId) => {
    try {
      const res = await messagingAPI.getOrCreateConversation(participantId);
      const conv = res.data.conversation;
      setConversations((prev) => {
        const exists = prev.find((c) => String(c._id) === String(conv._id));
        return exists ? prev : [conv, ...prev];
      });
      setActiveConvId(conv._id);
      return conv;
    } catch (err) {
      console.error('[openConversation]', err);
    }
  }, []);

  const createGroup = useCallback(async (groupName, participantIds) => {
    try {
      const res = await messagingAPI.createGroup(groupName, participantIds);
      const conv = res.data.conversation;
      setConversations((prev) => [conv, ...prev]);
      setActiveConvId(conv._id);
      return conv;
    } catch (err) {
      console.error('[createGroup]', err);
      toast.error(err?.response?.data?.message || 'Failed to create group');
    }
  }, []);

  const editGroup = useCallback(async (conversationId, newName) => {
    try {
      const res = await messagingAPI.editGroup(conversationId, newName);
      const updated = res.data.conversation;
      setConversations((prev) =>
        prev.map((c) => (String(c._id) === String(conversationId) ? { ...c, groupName: updated.groupName } : c))
      );
    } catch (err) {
      console.error('[editGroup]', err);
      toast.error(err?.response?.data?.message || 'Failed to rename group');
    }
  }, []);

  const deleteGroup = useCallback(async (conversationId) => {
    try {
      await messagingAPI.deleteGroup(conversationId);
      setConversations((prev) => prev.filter((c) => String(c._id) !== String(conversationId)));
      setMessages((prev) => { const next = { ...prev }; delete next[conversationId]; return next; });
      if (String(activeConvIdRef.current) === String(conversationId)) {
        setActiveConvId(null);
      }
    } catch (err) {
      console.error('[deleteGroup]', err);
      toast.error(err?.response?.data?.message || 'Failed to delete group');
    }
  }, []);

  const addGroupMember = useCallback(async (conversationId, memberId) => {
    try {
      const res = await messagingAPI.addGroupMember(conversationId, memberId);
      const updated = res.data.conversation;
      setConversations((prev) =>
        prev.map((c) => (String(c._id) === String(conversationId) ? { ...c, participants: updated.participants } : c))
      );
    } catch (err) {
      console.error('[addGroupMember]', err);
      toast.error(err?.response?.data?.message || 'Failed to add member');
    }
  }, []);

  const removeGroupMember = useCallback(async (conversationId, memberId) => {
    try {
      const res = await messagingAPI.removeGroupMember(conversationId, memberId);
      const updated = res.data.conversation;
      setConversations((prev) =>
        prev.map((c) => (String(c._id) === String(conversationId) ? { ...c, participants: updated.participants } : c))
      );
    } catch (err) {
      console.error('[removeGroupMember]', err);
      toast.error(err?.response?.data?.message || 'Failed to remove member');
    }
  }, []);

  const togglePin = useCallback(async (conversationId) => {
    try {
      const res = await messagingAPI.togglePin(conversationId);
      const pinned = res.data.pinned;
      setConversations((prev) =>
        prev.map((c) => (String(c._id) === String(conversationId) ? { ...c, pinned } : c))
      );
    } catch (err) {
      console.error('[togglePin]', err);
    }
  }, []);

  // ── Get all participant IDs for a conversation ─────────────────────────────
  const getParticipantIds = useCallback((conv) => {
    if (!conv?.participants) return [];
    return conv.participants.map((p) => String(p._id || p));
  }, []);

  // ── Send text / gif message ────────────────────────────────────────────────
  const sendMessage = useCallback(async (conversationId, plaintext, opts = {}) => {
    const currentUser = userRef.current;
    const myId = String(currentUser?.id || currentUser?._id || '');

    if (!plaintext?.trim()) { toast.error('Cannot send an empty message'); return; }
    if (!myId) { toast.error('Not logged in'); return; }
    if (!conversationId) { toast.error('No conversation selected'); return; }

    const conv = conversationsRef.current.find((c) => String(c._id) === String(conversationId));
    if (!conv) { toast.error('Conversation not found — try refreshing'); return; }

    const { ciphertext, nonce, senderPublicKey } = encryptMsg(plaintext, conversationId);

    const payload = {
      ciphertext, nonce, senderPublicKey,
      messageType: opts.messageType || 'text',
    };
    if (opts.gifUrl) payload.gifUrl = opts.gifUrl;
    if (opts.replyTo) payload.replyTo = opts.replyTo;
    if (opts.threadId) payload.threadId = opts.threadId;
    if (opts.mentions?.length) payload.mentions = opts.mentions;
    if (opts.linkPreviews?.length) payload.linkPreviews = opts.linkPreviews;

    try {
      const res = await messagingAPI.sendMessage(conversationId, payload);
      const msg = res.data.message;

      sentCache.current[msg._id] = plaintext;

      // Only add to main feed if not a thread reply
      if (!opts.threadId) {
        setMessages((prev) => {
          const existing = prev[conversationId] || [];
          if (existing.some((m) => m._id === msg._id)) return prev;
          return { ...prev, [conversationId]: [...existing, msg] };
        });
      }

      setConversations((prev) =>
        prev.map((c) =>
          String(c._id) === String(conversationId)
            ? { ...c, lastMessage: { senderId: myId, preview: 'Encrypted message', timestamp: new Date() } }
            : c
        )
      );

      return msg;
    } catch (err) {
      console.error('[sendMessage]', err);
      toast.error(err?.response?.data?.message || 'Failed to send message');
    }
  }, []);

  // ── Upload file / audio message ────────────────────────────────────────────
  const sendFileMessage = useCallback(async (conversationId, file, opts = {}) => {
    const currentUser = userRef.current;
    const myId = String(currentUser?.id || currentUser?._id || '');
    if (!myId || !conversationId) return;

    const conv = conversationsRef.current.find((c) => String(c._id) === String(conversationId));
    if (!conv) return;

    const caption = opts.caption || file.name || 'file';
    const { ciphertext, nonce, senderPublicKey } = encryptMsg(caption, conversationId);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('ciphertext', ciphertext);
    formData.append('nonce', nonce);
    formData.append('senderPublicKey', senderPublicKey);
    formData.append('messageType', opts.messageType || 'file');
    if (opts.replyTo) formData.append('replyTo', opts.replyTo);
    if (opts.threadId) formData.append('threadId', opts.threadId);
    if (opts.audioDuration) formData.append('audioDuration', opts.audioDuration);
    if (opts.mentions?.length) formData.append('mentions', JSON.stringify(opts.mentions));

    try {
      const res = await messagingAPI.uploadFile(conversationId, formData);
      const msg = res.data.message;
      sentCache.current[msg._id] = caption;

      if (!opts.threadId) {
        setMessages((prev) => {
          const existing = prev[conversationId] || [];
          if (existing.some((m) => m._id === msg._id)) return prev;
          return { ...prev, [conversationId]: [...existing, msg] };
        });
      }

      return msg;
    } catch (err) {
      console.error('[sendFileMessage]', err);
      toast.error('Failed to upload file');
    }
  }, []);

  // ── Toggle reaction ────────────────────────────────────────────────────────
  const toggleReaction = useCallback(async (conversationId, messageId, emoji) => {
    try {
      const res = await messagingAPI.toggleReaction(conversationId, messageId, emoji);
      setMessages((prev) => {
        const existing = prev[conversationId];
        if (!existing) return prev;
        return {
          ...prev,
          [conversationId]: existing.map((m) =>
            String(m._id) === String(messageId) ? { ...m, reactions: res.data.reactions } : m
          ),
        };
      });
    } catch (err) {
      console.error('[toggleReaction]', err);
    }
  }, []);

  // ── Load thread replies ────────────────────────────────────────────────────
  const loadThreadReplies = useCallback(async (conversationId, messageId) => {
    try {
      const res = await messagingAPI.getThreadReplies(conversationId, messageId);
      return res.data.messages || [];
    } catch (err) {
      console.error('[loadThreadReplies]', err);
      return [];
    }
  }, []);

  // ── Fetch link preview ─────────────────────────────────────────────────────
  const fetchLinkPreview = useCallback(async (url) => {
    try {
      const res = await messagingAPI.fetchLinkPreview(url);
      return res.data.preview;
    } catch {
      return null;
    }
  }, []);

  // ── Decrypt a message for display ─────────────────────────────────────────
  const decryptMessage = useCallback((msg, conversationId) => {
    const myId = String(userRef.current?.id || userRef.current?._id || '');
    if (!myId) return '...';

    const msgId = msg._id || msg.id;
    if (sentCache.current[msgId]) return sentCache.current[msgId];

    const convId = conversationId || activeConvIdRef.current;
    if (!convId) return '[Encrypted]';

    const result = decryptMsg(msg.ciphertext, msg.nonce, convId);
    if (result === null) return '[Unable to decrypt]';

    sentCache.current[msgId] = result;
    return result;
  }, []);

  // ── Broadcast status change ────────────────────────────────────────────────
  const broadcastStatusChange = useCallback(
    (status) => {
      if (!socketRef.current || !user) return;
      socketRef.current.emit('status_change', { userId: user.id, tenantId: user.tenantId, status });
    },
    [user]
  );

  // ── Emit typing ────────────────────────────────────────────────────────────
  const emitTyping = useCallback((conversationId, isTyping, threadId) => {
    if (!socketRef.current || !user) return;
    socketRef.current.emit('typing', { conversationId, userId: user.id, isTyping, threadId });
  }, [user]);

  // Sort conversations: pinned first, then by last message timestamp
  const sortedConversations = [...conversations].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    const ta = a.lastMessage?.timestamp ? new Date(a.lastMessage.timestamp) : new Date(a.createdAt);
    const tb = b.lastMessage?.timestamp ? new Date(b.lastMessage.timestamp) : new Date(b.createdAt);
    return tb - ta;
  });

  const value = {
    directory,
    conversations: sortedConversations,
    activeConvId,
    setActiveConvId,
    messages,
    loadingMessages,
    totalUnread,
    openConversation,
    createGroup,
    editGroup,
    deleteGroup,
    addGroupMember,
    removeGroupMember,
    togglePin,
    sendMessage,
    sendFileMessage,
    toggleReaction,
    loadThreadReplies,
    fetchLinkPreview,
    decryptMsg: decryptMessage,
    refreshConversations,
    loadDirectory,
    socketRef,
    socketConnected,
    broadcastStatusChange,
    emitTyping,
    encryptMsg: (plaintext, convId) => encryptMsg(plaintext, convId),
  };

  return <MessagingContext.Provider value={value}>{children}</MessagingContext.Provider>;
};
